// A DATE FIELD IS LOCAL MIDNIGHT OF THE DAY — constructed here, once, for every write.
//
// The platform's standard is written down at its source (tkt-backend utils/absoluteDateTime.ts):
// a `date` field carries ONLY the calendar day, stored as a real absolute instant — the
// organizer's own LOCAL MIDNIGHT of that day. The clock time travels separately in its own
// `*Time` field as a bare "HH:MM" offset, and the real moment is always `date + time`, added by
// the reader. An organizer in Kampala picking 15 Sep at 19:00 sends date "2026-09-14T21:00:00Z"
// and startTime "19:00" — not one combined instant.
//
// Two things go wrong when a form doesn't know that, and only one of them is loud:
//
//   Bare "YYYY-MM-DD" — rejected at the backend's zod boundary, a 400 you cannot miss. This is
//   what made editing an event's date fail while saving it unchanged worked (the unchanged path
//   re-sent the already-instant stored value).
//
//   Date COMBINED with its own time — accepted by zod, which only checks the shape, and then
//   counted twice by every reader that adds the time again. Measured on live rows: "Venue Link
//   Check" stored 15 Sep 16:00 with startTime 16:00, so the backend computes its start as 16 Sep
//   08:00 — a day late, which quietly drops it out of "today" and mis-ranks it on the public
//   grid. Every evening event created through the wizard carried this. Silence is the whole
//   problem: the loud bug got fixed three times in three files, the silent one shipped.
//
// So there is ONE conversion, and it needs no time at all — that is the simplification. It is
// applied in `writeJson`, the single place this app turns a request body into JSON, so a form
// hands over whatever its <input type="date"> produced and is correct by construction. Nothing
// to remember, nothing to import, and a form written next year inherits it without knowing it
// exists. Before this the same conversion existed three times (lib/eventDate, NewEventWizard,
// EventSaleWindowPanel's toMidnightInstant) and was simply absent from NewPromoCodeModal.

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
const BARE_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** True when the value is already the absolute instant the backend accepts. */
export const isAbsoluteIsoInstant = (value) => ISO_INSTANT.test(String(value || ''));

/**
 * A calendar day → the operator's LOCAL MIDNIGHT of that day, as an absolute instant.
 *
 * Passes through anything already an instant: re-deriving one would shift the event by the
 * browser's offset on every save, walking its date backwards an hour at a time. Passes through
 * anything unrecognised too — inventing a timestamp for a value we cannot read would turn a
 * clear server-side rejection into a silently wrong date, and the server is the right place for
 * that argument. Local midnight, not UTC midnight, is the point: `new Date(y, m-1, d)` reads the
 * operator's own zone, which is the organizer's, which is what the standard means by "their day".
 */
export const toLocalMidnightInstant = (value) => {
  const day = String(value ?? '').trim();
  if (!day || isAbsoluteIsoInstant(day) || !BARE_DAY.test(day)) return value;
  const [y, m, d] = day.split('-').map(Number);
  const at = new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(at.getTime()) ? value : at.toISOString();
};

/**
 * THE INVERSE — an absolute instant → the operator's local calendar day, for an
 * <input type="date">. It has to read the LOCAL day, never `slice(0, 10)`: local midnight of
 * 3 Aug in Kampala is stored "2026-08-02T21:00:00.000Z", so slicing shows the 2nd — every row,
 * both ends, one day early, for every timezone east of UTC. It looked fine because the clock
 * beside it came from a separate wall-clock field, so the row showed one day's date next to
 * another day's time. Paired with `toLocalMidnightInstant`, the form round-trips exactly.
 */
export const toLocalDay = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (!raw.includes('T')) return raw.slice(0, 10);   // already a bare calendar day
  const at = new Date(raw);
  if (Number.isNaN(at.getTime())) return raw.slice(0, 10);
  const p = (n) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${p(at.getMonth() + 1)}-${p(at.getDate())}`;
};

/** Today, as the operator's own calendar day — the default a form means by "from now". */
export const todayLocalDay = () => toLocalDay(new Date().toISOString());

/**
 * The fields under the contract — every payload key the backend guards with
 * `isAbsoluteIsoInstant`, read off its zod schemas: event pages (`date`), promo codes and
 * discounts (`startDate`/`endDate`), schedules (`startDate`/`endDate`), schedule slots
 * (`startDateTime`/`endDateTime`), ticket types (`salesStartDate`/`salesEndDate`).
 *
 * Names, not paths, because that is what the contract is actually keyed on: the backend applies
 * the same refinement to the same name wherever it appears, so a path table would be a second
 * list to keep in step with the first. Top-level only — every guarded field is a top-level key,
 * and a body's nested objects (metadata, address, games) are free-form JSON the backend does not
 * type. Safe by construction anyway: a value that is already an instant, or is not a bare day,
 * is returned untouched, so a same-named field that means something else is never rewritten.
 */
const INSTANT_FIELDS = ['date', 'startDate', 'endDate', 'startDateTime', 'endDateTime', 'salesStartDate', 'salesEndDate'];

/** Returns a body whose date fields satisfy the contract. Returns the same object if none did. */
export const withAbsoluteInstants = (body) => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
  let next = body;
  for (const field of INSTANT_FIELDS) {
    if (!(field in body)) continue;
    const instant = toLocalMidnightInstant(body[field]);
    if (instant === body[field]) continue;
    if (next === body) next = { ...body };
    next[field] = instant;
  }
  return next;
};
